import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';
import { useElevation } from '@/shared/elevation/useElevation';

import { type EmployeeListParams, payrollApi, payrollKeys } from './api';
import {
  type PayrollBatchDetail,
  type PayrollEmployee,
  type PayrollEmployeeCreated,
  type PayrollEmployeeUpdate,
  type PayrollEmployeeWrite,
  type PayrollImportResult,
  type PayrollPasswordIssued,
  WORKING,
} from './types';

/** While the worker has a batch in its hands, or a test is on its way, the screen asks
 * again this often; otherwise once a minute, which is how often a new month may come. */
const WORKING_MS = 2_000;
const IDLE_MS = 60_000;

export function usePayrollStatus() {
  return useQuery({ queryKey: payrollKeys.status, queryFn: payrollApi.status });
}

export function usePayrollBatches(offset: number) {
  return useQuery({
    queryKey: payrollKeys.batchList(offset),
    queryFn: () => payrollApi.batches(offset),
    refetchInterval: (query) =>
      query.state.data?.items.some((batch) => WORKING.has(batch.status)) ? WORKING_MS : IDLE_MS,
  });
}

export function usePayrollBatch(id: string | undefined) {
  return useQuery<PayrollBatchDetail, ApiError>({
    queryKey: payrollKeys.batch(id ?? ''),
    queryFn: () => payrollApi.batch(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const batch = query.state.data;
      if (!batch) return false;
      return WORKING.has(batch.status) || batch.last_test?.status === 'sending'
        ? WORKING_MS
        : false;
    },
  });
}

/**
 * Every write on a batch answers with the batch as it is now: into the cache, and the
 * list and the overview asked again (the counts and the states in them change). A refusal
 * (a 409: «Esa ya ha salido», «Ahora son 20, no 21») asks the batch again too, so the
 * screen shows what changed.
 */
function useBatchMutation<TVariables>(
  id: string | null,
  run: (variables: TVariables) => Promise<PayrollBatchDetail>,
  { elevated = false }: { elevated?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const { withElevation } = useElevation();
  return useMutation<PayrollBatchDetail, ApiError | Error, TVariables>({
    mutationFn: (variables) => (elevated ? withElevation(() => run(variables)) : run(variables)),
    onSuccess: (batch) => {
      queryClient.setQueryData(payrollKeys.batch(batch.id), batch);
      void queryClient.invalidateQueries({ queryKey: payrollKeys.batches });
    },
    onError: () => {
      if (id) void queryClient.invalidateQueries({ queryKey: payrollKeys.batch(id) });
    },
  });
}

export function useUploadPayroll() {
  return useBatchMutation<File>(null, (file) => payrollApi.upload(file));
}

export function useGivePassword(id: string) {
  return useBatchMutation<{ password: string; remember: boolean }>(id, ({ password, remember }) =>
    payrollApi.givePassword(id, password, remember),
  );
}

export function useSetPeriod(id: string) {
  return useBatchMutation<string>(id, (period) => payrollApi.setPeriod(id, period));
}

export function useAcceptBatch(id: string) {
  return useBatchMutation<void>(id, () => payrollApi.accept(id));
}

export function useDiscardBatch(id: string) {
  return useBatchMutation<void>(id, () => payrollApi.discard(id));
}

export function useRetryBatch(id: string) {
  return useBatchMutation<void>(id, () => payrollApi.retry(id));
}

export function useUpdateSlip(id: string) {
  return useBatchMutation<{ slipId: string; employee_id?: string | null; excluded?: boolean }>(
    id,
    ({ slipId, ...change }) => payrollApi.updateSlip(id, slipId, change),
  );
}

export function useSendTest(id: string) {
  return useBatchMutation<void>(id, () => payrollApi.test(id));
}

/**
 * «Enviar»: the password again (the API asks for the elevated mode) and the number the
 * person was shown. A 409 `count_mismatch` means something changed in between: the batch
 * is asked again, so the screen shows the new number.
 */
export function useSendPayroll(id: string) {
  return useBatchMutation<number>(id, (count) => payrollApi.send(id, count), { elevated: true });
}

export function useEmployees(params: EmployeeListParams) {
  return useQuery({
    queryKey: payrollKeys.employeeList(params),
    queryFn: () => payrollApi.employees(params),
  });
}

/**
 * A change in the list of employees changes what a batch says (who is ready, the
 * blockers of «Enviar») and the overview: everything of the payroll is asked again.
 */
function useEmployeeMutation<TData, TVariables>(
  run: (variables: TVariables) => Promise<TData>,
  { elevated = false }: { elevated?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const { withElevation } = useElevation();
  return useMutation<TData, ApiError | Error, TVariables>({
    mutationFn: (variables) => (elevated ? withElevation(() => run(variables)) : run(variables)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

export function useCreateEmployee() {
  return useEmployeeMutation<PayrollEmployeeCreated, PayrollEmployeeWrite>((payload) =>
    payrollApi.createEmployee(payload),
  );
}

/**
 * Changing where a payslip goes (the address, or the NIF that decides whose it is) asks
 * for the password again, like «Enviar»: the prompt only shows when the API says so, so
 * a name change goes straight through.
 */
export function useUpdateEmployee() {
  return useEmployeeMutation<PayrollEmployee, { id: string; payload: PayrollEmployeeUpdate }>(
    ({ id, payload }) => payrollApi.updateEmployee(id, payload),
    { elevated: true },
  );
}

export function useNewEmployeePassword() {
  return useEmployeeMutation<PayrollPasswordIssued, string>((id) => payrollApi.newPassword(id), {
    elevated: true,
  });
}

/** The list can change addresses and NIFs: the password again, as for one person. */
export function useImportEmployees() {
  return useEmployeeMutation<PayrollImportResult, File>(
    (file) => payrollApi.importEmployees(file),
    { elevated: true },
  );
}
